/**
 * Support access on a restaurant's page: whether its owner has let support
 * in, and opening its back office when they have.
 *
 * The consent is the owner's, given and ended in their back office; nothing
 * here can give it. With a live grant, "Open their back office" starts a
 * session and opens the restaurant's back office in a new tab, with a
 * one-time code in the address that the back office spends at once. The
 * owners are emailed that it started, and the session is on both logs.
 */

import {
  ApiError,
  displayLocale,
  useOpenSupportSession,
  useRestaurantSupport,
  type PlatformSupportGrant,
} from '@restaurant/api-client';
import { ActionError, Badge, Button, Card, ErrorNotice, Loading } from '@restaurant/ui';

import { formatMoment } from './words';

export function SupportCard({ restaurantId }: { restaurantId: string }) {
  const support = useRestaurantSupport(restaurantId);
  const open = useOpenSupportSession();
  const locale = displayLocale();
  const current = support.data?.current ?? null;

  const start = () => {
    // Opened now, on the click, so no popup blocker takes it; pointed at the
    // link once the server has made the session.
    const tab = window.open('about:blank', '_blank');
    open.mutate(restaurantId, {
      onSuccess: (opened) => {
        if (tab) {
          tab.opener = null;
          tab.location.href = opened.url;
        } else {
          window.location.assign(opened.url);
        }
      },
      onError: () => tab?.close(),
    });
  };

  return (
    <Card
      title="Support access"
      actions={
        current && (
          <Button variant="primary" loading={open.isPending} onClick={start}>
            Open their back office
          </Button>
        )
      }
    >
      {support.isPending && <Loading label="Loading" />}
      {support.isError && <ErrorNotice message={support.error.message} />}
      {support.data &&
        (current ? (
          <Granted grant={current} locale={locale} />
        ) : (
          <p className="pf__sub">
            Not granted. An owner turns it on in their back office, under Settings → Support
            access; until then their back office cannot be opened from here.
          </p>
        ))}
      {open.data && (
        <p className="pf__sub">
          Session opened until {formatMoment(open.data.expires_at, locale)},{' '}
          {open.data.read_only ? 'read-only' : 'with changes allowed'}.{' '}
          {open.data.mailed_to.length > 0
            ? `The owners were told (${open.data.mailed_to.join(', ')}).`
            : 'No owner could be emailed.'}
        </p>
      )}
      {open.error instanceof ApiError && (
        <ActionError error={open.error} action="Opening their back office" />
      )}
      {support.data && support.data.grants.some((g) => g.sessions.length > 0) && (
        <Visits grants={support.data.grants} locale={locale} />
      )}
    </Card>
  );
}

function Granted({ grant, locale }: { grant: PlatformSupportGrant; locale: string }) {
  return (
    <dl className="pf__facts">
      <dt>Granted</dt>
      <dd>
        <Badge tone="warn">Until {formatMoment(grant.expires_at, locale)}</Badge>
      </dd>
      <dt>May</dt>
      <dd>{grant.allow_changes ? 'Look and make changes' : 'Look only (read-only)'}</dd>
      <dt>By</dt>
      <dd>
        {grant.granted_by_name}, {formatMoment(grant.granted_at, locale)}
      </dd>
      <dt>Their note</dt>
      <dd>{grant.note ? `“${grant.note}”` : '—'}</dd>
    </dl>
  );
}

function Visits({ grants, locale }: { grants: PlatformSupportGrant[]; locale: string }) {
  const visits = grants.flatMap((g) => g.sessions).slice(0, 10);
  return (
    <ul className="pf__list">
      {visits.map((v) => (
        <li key={v.id}>
          <span className="pf__faint">{formatMoment(v.started_at, locale)}</span>{' '}
          {v.operator_name}, {v.read_only ? 'read-only' : 'with changes'}
          {!v.opened && ' — link never opened'}
          {v.ended_how === 'operator' && ' — ended by support'}
          {v.ended_how === 'owner' && ' — ended by the owner'}
        </li>
      ))}
    </ul>
  );
}
